<template>
  <div class="gc-multiple-choice gc-mb-4">
    <gc-row>
      <gc-col
        v-for="option in filteredOptions"
        :key="option.value"
        :cols="isMd ? 12 / columns : 12"
        class="gc-py-0 gc-flex"
      >
        <div
          class="gc-multiple-choice-option gc-w-full gc-p-3 gc-mb-2 gc-flex gc-items-center gc-rounded gc-border-2 gc-transition-all gc-duration-150 gc-ease-in-out gc-cursor-pointer"
          :class="{
            'gc-single-answer': singleAnswer,
            'gc-bg-white gc-border-primary': value.includes(option.value),
            'gc-border-gray-100 hover:gc-border-gray-300': !value.includes(
              option.value,
            ),
            'gc-justify-center': verticalOptionLayout,
          }"
          @click="selectOption(option.value)"
          data-test-id="option"
        >
          <div
            class="gc-flex gc-justify-start gc-items-center"
            :class="{ 'gc-flex-col-reverse': verticalOptionLayout }"
          >
            <multiple-choice-checkbox
              :selected="value.includes(option.value)"
              :single-answer="singleAnswer"
              :class="{
                'gc-mt-2': verticalOptionLayout,
                'gc-mr-3': !verticalOptionLayout,
              }"
              v-if="!disableUnselect && !hideCheckbox"
            />

            <div>
              <slot name="customOption" :option="option">
                <div
                  class="gc-flex gc-justify-start gc-items-center"
                  :class="{ 'gc-flex-col': verticalOptionLayout }"
                >
                  <div
                    v-if="option.icon"
                    :class="{
                      'gc-mb-2': verticalOptionLayout,
                      'gc-mr-2': !verticalOptionLayout,
                    }"
                  >
                    <font-awesome-icon
                      :icon="option.icon"
                      size="2x"
                      class="gc-text-primary"
                    />
                  </div>

                  <div
                    v-if="option.image"
                    :class="{
                      'gc-mb-2': verticalOptionLayout,
                      'gc-mr-2': !verticalOptionLayout,
                    }"
                  >
                    <gc-image
                      :src="`${baseUrl}/${option.image.file}`"
                      :height="option.image.options.height"
                      :width="option.image.options.width"
                    />
                  </div>
                  <div
                    class="gc-multiple-choice-option__content"
                    :class="{ 'gc-text-center': verticalOptionLayout }"
                  >
                    <span class="text-subtitle-1 gc-font-bold">
                      {{
                        $t(
                          getOptionLabelString(
                            assistantStep.id,
                            fieldName,
                            option.value,
                          ),
                        )
                      }}
                    </span>
                    <small
                      v-if="
                        $te(
                          getOptionDescriptionString(
                            assistantStep.id,
                            fieldName,
                            option.value,
                          ),
                        )
                      "
                      class="gc-block gc-text-opacity-75"
                    >
                      {{
                        $t(
                          getOptionDescriptionString(
                            assistantStep.id,
                            fieldName,
                            option.value,
                          ),
                        )
                      }}
                    </small>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </gc-col>
    </gc-row>

    <div
      v-if="enableCustomOption"
      class="gc-flex gc-mutliple-choice-option gc-px-0 gc-pt-2"
    >
      <gc-text-field
        v-model="customOption"
        :label="$t(`customOption`)"
        dense
        outlined
        class="gc-mb-0"
        @focus="handleInputFocus()"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { VInput } from 'vuetify/lib';
import { PropType, computed, ref, watch } from '@vue/composition-api';
import GcCard from '@/components/primitives/GcCard.vue';
import GcImage from '@/components/primitives/GcImage.vue';
import MultipleChoiceCheckbox from '@/components/MultipleChoiceCheckbox.vue';
import GcTextField from '@/components/primitives/GcTextField.vue';
import {
  MultipleChoiceOption,
  StepDefinition,
} from '@/interfaces/step-definition';
import { useResponsiveBreakpoints } from '@/lib/ui-helper/responsive';
import GcCol from '@/components/primitives/GcCol.vue';
import GcRow from '@/components/primitives/GcRow.vue';
import {
  getOptionDescriptionString,
  getOptionLabelString,
} from '@/lib/forms/helper';
import { AssistantContext } from '~/steps/steps';
import { FormMultipleChoiceComponentConfig } from '@/interfaces/step-definition';

const checkPropCompatibility = (props: FormMultipleChoiceComponentConfig) => {
  if (props.nextStep && !props.singleAnswer) {
    throw Error(
      'properties nextStep=true and singleAnswer=false are incompatible',
    );
  }
};

export default VInput.extend({
  components: {
    GcCard,
    GcCol,
    GcImage,
    GcRow,
    GcTextField,
    MultipleChoiceCheckbox,
  },
  props: {
    assistantContext: {
      default: () => ({}),
      type: Object as PropType<AssistantContext>,
    },
    assistantStep: {
      required: true,
      type: Object as PropType<StepDefinition>,
    },
    columns: {
      default: 1,
      type: Number,
    },
    disableUnselect: {
      default: false,
      type: Boolean,
    },
    hideCheckbox: {
      default: false,
      type: Boolean,
    },
    enableCustomOption: {
      default: false,
      type: Boolean,
    },
    fieldName: {
      required: true,
      type: String,
    },
    label: {
      default: '',
      type: String,
    },
    nextStep: {
      default: false,
      type: Boolean,
    },
    options: {
      type: Array as PropType<Array<MultipleChoiceOption>>,
    },
    singleAnswer: {
      default: false,
      type: Boolean,
    },
    verticalOptionLayout: {
      default: false,
      type: Boolean,
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setup: (props: any, { emit }) => {
    checkPropCompatibility(props);

    const baseUrl = process.env.VUE_APP_BASE_URL;

    const { isMd } = useResponsiveBreakpoints();

    const filteredOptions = computed(() =>
      (props.options as Array<MultipleChoiceOption>).filter((option) =>
        option.isVisible !== undefined && typeof option.isVisible === 'function'
          ? option.isVisible(props.assistantContext)
          : true,
      ),
    );

    const getCustomValue = () => {
      if (!props.value || props.value.length === 0) {
        return '';
      }
      const customValues = props.value.filter(
        (v: string) =>
          !props.options?.map((o: MultipleChoiceOption) => o.value).includes(v),
      );
      return customValues.length > 0 ? customValues[0] : '';
    };

    const customOption = ref<string>(getCustomValue());
    const customOptionSelected = computed(
      () =>
        props.enableCustomOption &&
        customOption.value.length > 0 &&
        props.value.includes(customOption.value),
    );

    watch(props, () => {
      if (
        props.singleAnswer &&
        customOption.value.length > 0 &&
        props.value[0] !== customOption.value
      ) {
        customOption.value = '';
      }
    });

    watch(customOption, (val, oldVal) => {
      if (
        val.length === 0 &&
        props.value.length > 0 &&
        props.options
          .map((o: MultipleChoiceOption) => o.value)
          .includes(props.value[0])
      ) {
        return;
      }
      if (props.singleAnswer) {
        emit('input', val.trim().length > 0 ? [val] : []);
      } else {
        const oldValueIndex = props.value.findIndex(
          (v: string) => v === oldVal.trim(),
        );
        let updatedArray = [...props.value];
        if (oldValueIndex > -1) {
          updatedArray[oldValueIndex] = val.trim();
        } else {
          updatedArray.push(val.trim());
        }
        updatedArray = updatedArray.filter((v) => v.trim().length > 0);
        emit('input', updatedArray);
      }
    });

    const selectOption = (id: string) => {
      if (id.length === 0) {
        emit('input', []);
        return;
      }

      if (props.singleAnswer && props.disableUnselect) {
        emit('input', [id]);
        return;
      }

      const oldValueIndex = props.value.findIndex((v: string) => v === id);
      const updatedArray = [...props.value];
      if (props.singleAnswer) {
        updatedArray.splice(0);
        if (oldValueIndex === -1) {
          updatedArray.push(id);
        }
      } else if (oldValueIndex > -1) {
        updatedArray.splice(oldValueIndex, 1);
      } else {
        updatedArray.push(id);
      }
      emit('input', updatedArray);

      if (props.nextStep) {
        emit('next-step');
      }
    };

    const handleInputFocus = () => {
      if (
        props.singleAnswer &&
        props.value.length > 0 &&
        customOption.value.length === 0
      ) {
        emit('input', []);
      }
    };

    return {
      baseUrl,
      customOption,
      customOptionSelected,
      filteredOptions,
      getOptionDescriptionString,
      getOptionLabelString,
      handleInputFocus,
      isMd,
      selectOption,
    };
  },
});
</script>
<style lang="scss">
.gc-multiple-choice {
  .row {
    margin-top: 0;
    margin-bottom: 0;
  }
  .v-text-field__details {
    display: none !important;
  }
}
</style>
